import React from 'react';
import CampaignTile from '../../Admin/Campaigns/analytics/CampaignTile';
import {
  AnalyticsChartType,
  DEFAULT_CHART_SETTINGS,
} from '../../../support/campaignAnalytics';
import type { ChartPoint } from '../../../support/campaignLeaderCharts';

/**
 * A titled card wrapping one Leader chart. Reuses the same inline tile renderer
 * (`CampaignTile` → `DataVisualizationComponent`) the rest of the app draws dashboard tiles with, so
 * these charts look identical to the config-driven tiles on the other Leader domains — but here the
 * `{ label, value }` rows are computed client-side from the delivery/enrollment rollups. Renders an
 * empty-state note (at the same fixed height) instead of a blank chart when every value is zero, so a
 * member with no activity reads clearly rather than as a broken tile.
 *
 * The card chrome intentionally matches the polished Admin Campaigns analytics dashboard
 * (`Admin/Campaigns/analytics/dashboardParts.tsx` + `analyticsDashboard.module.css`): 16px radius, a
 * soft card shadow, a divided header, and a padded body. Those styles live behind `--cb-*` tokens that
 * are scoped to the Campaign Builder subtree, so the equivalent literal values are inlined here as the
 * shared `DASH` constants below and reused by every Leader tab.
 */

/** Literal copies of the Admin analytics `--cb-*` design tokens (scoped to `.builderRoot` there, so
 *  they can't be referenced from the Leader subtree). Keep these in sync with
 *  `Admin/Campaigns/analytics` if that palette changes. */
export const DASH = {
  surface: '#ffffff',
  surfaceAlt: '#f3f4f6',
  border: '#e5e7eb',
  text: '#1a2332',
  textDim: '#6b7280',
  textFaint: '#9ca3af',
  accent: '#049fd9',
  radius: 16,
  shadowHairline: '0 1px 4px rgba(0,0,0,0.04)',
  shadowCard: '0 2px 8px rgba(0,0,0,0.06)',
  gridGap: 16,
  kpiGap: 14,
  chartMinCol: 340,
  kpiMinCol: 150,
  /** Default rendered height of a chart body (px). Matches the analytics dashboard default. */
  chartHeight: 300,
} as const;

interface Props {
  title: string;
  subtitle?: string;
  chartType: AnalyticsChartType;
  data: ChartPoint[];
  tileKey: string;
  /** Rendered height of the chart body in px. Keep uniform within a row for an aligned grid. */
  height?: number;
  /** Span the whole grid row (for wide bars / tables). Mirrors the analytics `.gridFull` capability. */
  full?: boolean;
  /** Extra chart settings merged over the default `{ labelField, valueField }`. */
  settings?: Record<string, any>;
  /**
   * Tooltip key titles. Leader charts always show a two-row `Label: value` tooltip (never the default
   * three-row Series/Label/Value), so these name the two keys after the data they represent —
   * e.g. `{ label: 'Status', value: 'Enrollments' }` for the completion donut. Defaults to
   * `Label`/`Value` when omitted.
   */
  tooltipKeys?: { label?: string; value?: string };
  empty?: string;
}

/** A responsive card grid that keeps charts from clipping (min 340px per chart) with 16px gutters.
 *  Uses `auto-fit` (not `auto-fill`) so a small number of charts stretch to fill the row instead of
 *  collapsing to their min width against the left edge — the "crammed to the left" layout bug.
 *  `gridAutoRows: 1fr` keeps rows uniform so cards of equal configured height line up cleanly. */
export const ChartGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: 'grid',
      gap: DASH.gridGap,
      marginTop: 12,
      gridTemplateColumns: `repeat(auto-fit, minmax(${DASH.chartMinCol}px, 1fr))`,
      gridAutoRows: '1fr',
      alignItems: 'stretch',
    }}
  >
    {children}
  </div>
);

/** Compact scalar KPI cards — value-on-top, uppercase label — matching the analytics `KpiStrip`. */
export const KpiCards: React.FC<{ kpis: { label: string; value: React.ReactNode }[] }> = ({ kpis }) => (
  <div
    style={{
      display: 'grid',
      gap: DASH.kpiGap,
      gridTemplateColumns: `repeat(auto-fit, minmax(${DASH.kpiMinCol}px, 1fr))`,
    }}
  >
    {kpis.map((k) => (
      <div
        key={k.label}
        style={{
          background: DASH.surface,
          border: `1px solid ${DASH.border}`,
          borderRadius: DASH.radius,
          boxShadow: DASH.shadowHairline,
          padding: '16px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <div
          style={{
            fontSize: '1.75rem',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            color: DASH.text,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {k.value}
        </div>
        <div
          style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            color: DASH.textDim,
          }}
        >
          {k.label}
        </div>
      </div>
    ))}
  </div>
);

export default function CampaignChartCard({
  title,
  subtitle,
  chartType,
  data,
  tileKey,
  height = DASH.chartHeight,
  full = false,
  settings,
  tooltipKeys,
  empty = 'No data yet.',
}: Props) {
  const hasData = data.some((d) => (d.value ?? 0) !== 0);
  // Leader charts show a two-key tooltip (`labelValue`), keyed to what the data means. Callers'
  // `settings` win over the mode default, but the explicit `tooltipKeys` titles win over both.
  const tooltipSettings = {
    tooltipMode: 'labelValue',
    ...(settings || {}),
    ...(tooltipKeys?.label ? { tooltipLabelTitle: tooltipKeys.label } : {}),
    ...(tooltipKeys?.value ? { tooltipValueTitle: tooltipKeys.value } : {}),
  };
  return (
    <div
      style={{
        background: DASH.surface,
        border: `1px solid ${DASH.border}`,
        borderRadius: DASH.radius,
        boxShadow: DASH.shadowCard,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
        ...(full ? { gridColumn: '1 / -1' } : {}),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '14px 18px',
          borderBottom: `1px solid ${DASH.border}`,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: '0.875rem',
              fontWeight: 700,
              color: DASH.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={title}
          >
            {title}
          </div>
          {subtitle ? (
            <div style={{ fontSize: '0.75rem', color: DASH.textDim, marginTop: 2 }}>{subtitle}</div>
          ) : null}
        </div>
      </div>
      <div style={{ padding: '12px 14px', minWidth: 0, flex: 1, display: 'flex' }}>
        {hasData ? (
          <div style={{ width: '100%' }}>
            <CampaignTile
              chartType={chartType}
              data={data}
              settings={{ ...DEFAULT_CHART_SETTINGS, ...tooltipSettings }}
              height={height}
              tileKey={tileKey}
            />
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              height,
              minHeight: height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: DASH.textDim,
              fontSize: '0.875rem',
            }}
          >
            {empty}
          </div>
        )}
      </div>
    </div>
  );
}
